import React from 'react';
import styled from '@emotion/styled';
import { globalPadding } from '../../../../Layout/Layout';
import { headerHeight } from '../../../../Header';
import ContentPlayer from '../../../SingleContent/ContentPlayer';
import ContentHeaderCover from '../../../SingleContent/ContentHeaderCover';
import { TeaserDataType } from '../../../SingleContent/ContentHeader';

// The teaser is just the video. The title/body are page-level content (CompactHeader in compact mode,
// otherwise a title above the widgets); `compact` only makes the cover go under the header.
export type TeaserHeaderInfo = {
  compact?: boolean;
};

const COVER_HEIGHT = 'min(calc(100vw / 1.4), 38rem)';

export default function TeaserVideo({
  items,
  displayTitle,
  compact,
}: {
  items: any;
  displayTitle?: string;
  compact?: boolean;
}) {
  const item = items?.[0];
  const filesInfo = item?.file?.files_info;
  const isHls = filesInfo?.video_type === 'hls';
  const videoInfo = isHls ? filesInfo?.hls_versions?.[0] : filesInfo?.mp4_versions?.[0];

  const teaserData: TeaserDataType = {
    videoUrl: videoInfo?.url ?? '',
    posterAlt: displayTitle || videoInfo?.display_title || 'Filmnet',
    poster: item?.poster?.path ?? item?.file?.poster?.path ?? '',
    video_type: isHls ? 'hls' : 'mp4',
  };

  if (!teaserData.videoUrl) return null;

  if (compact) {
    return (
      <CompactBleed>
        <ContentHeaderCover teaserData={teaserData} coverHeight={COVER_HEIGHT} />
      </CompactBleed>
    );
  }

  return (
    <FullWidthBleed>
      <ContentPlayer teaserData={teaserData} showGradient={false} fixedHeight="100%" />
    </FullWidthBleed>
  );
}

// Pulls the cover up under the fixed (transparent) header and edge-to-edge,
// so it starts from the top of the page exactly like the single content page.
// position/zIndex lift the teaser above the next widget: a hover-overflow carousel pulls its
// 3rem top padding up (marginTop: -3rem) over the teaser's bottom, where the controls sit, and
// would otherwise intercept their clicks.
const CompactBleed = styled.div(({ theme }) => ({
  position: 'relative',
  zIndex: 1,
  ...theme.mixins.create(globalPadding, (p) => ({ margin: `calc(-1 * (${headerHeight} + ${theme.spacing(3)})) -${theme.spacing(p)} 0` })),
  // ContentHeaderCover (.header-cover) carries a breakpoint-dependent bottom margin meant for the
  // single-content layout, where absolutely-positioned metadata hangs into that gap — it even goes
  // negative on lg/xl. The teaser renders no such metadata, so that margin would pull *every*
  // following widget up under the cover. Drop it on all breakpoints so the teaser reserves its true
  // height and widgets after it flow cleanly below; only the live widget pulls itself back up onto
  // the teaser, and it does that on its own (see LiveCardList's `afterTeaser`). Scoped to the teaser.
  '& .header-cover': {
    marginBottom: 0,
  },
}));

// Full-width banner for the non-compact header. position/zIndex keep the controls above the
// next widget's overlap (see CompactBleed).
const FullWidthBleed = styled.div(({ theme }) => ({
  position: 'relative',
  zIndex: 1,
  height: COVER_HEIGHT,
  ...theme.mixins.create(globalPadding, (p) => ({ margin: `0 -${theme.spacing(p)}` })),
}));
